'use client'

import * as React from 'react'
import * as AvatarPrimitive from '@radix-ui/react-avatar'

import { cn } from '../../../lib/utils'
import { resolveAvatarImageUrl } from './resolveAvatarImageUrl'

export type AvatarProps = {
  img?: string
  name?: string
  initials?: string
  seed?: string // Optional seed for DiceBear
}

type AvatarRootProps = React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Root
>

const Avatar: React.FC<AvatarRootProps> = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarRootProps
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full items-center justify-center bg-muted text-muted-foreground',
      className,
    )}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

type AvatarImageProps = React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Image
> & {
  seed?: string
}

const AvatarImage: React.FC<AvatarImageProps> = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  AvatarImageProps
>(({ className, seed, src, ...props }, ref) => {
  const imageUrl = resolveAvatarImageUrl(
    typeof src === 'string' ? src : undefined,
    seed,
    true,
  )

  return (
    <AvatarPrimitive.Image
      ref={ref}
      src={imageUrl}
      className={cn('aspect-square h-full w-full object-contain', className)}
      {...props}
    />
  )
})
AvatarImage.displayName = AvatarPrimitive.Image.displayName

type AvatarFallbackProps = React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Fallback
>

const AvatarFallback: React.FC<AvatarFallbackProps> = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center rounded-full bg-muted',
      className,
    )}
    {...props}
  />
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }
